import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * CBTWorksheet Component
 * Interactive Cognitive Behavioral Therapy worksheet tool
 * Tablet-optimized responsive design with touch-friendly interface
 * References GitHub best practices for CBT digital tools
 */

const CBTWorksheet = () => {
  const navigate = useNavigate();
  const worksheetRef = useRef(null);
  
  // State management for worksheet types and data
  const [activeWorksheet, setActiveWorksheet] = useState('thoughtRecord');
  const [isSaving, setIsSaving] = useState(false);
  const [autoSave, setAutoSave] = useState(true);
  const [savedWorksheets, setSavedWorksheets] = useState([]);
  const [notification, setNotification] = useState(null);

  // Thought Record State
  const [thoughtRecord, setThoughtRecord] = useState({
    date: new Date().toISOString().split('T')[0],
    time: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
    situation: '',
    automaticThoughts: '',
    emotions: [],
    emotionIntensity: {},
    evidence: { for: '', against: '' },
    balancedThought: '',
    emotionReassessment: {},
    behavioralPlan: ''
  });

  // ABC Model State
  const [abcModel, setAbcModel] = useState({
    activatingEvent: '',
    beliefs: '',
    consequences: '',
    dispute: '',
    effectiveNewBelief: ''
  });

  // Behavioral Activation State
  const [behavioralActivation, setBehavioralActivation] = useState({
    activities: [],
    moodBefore: 5,
    moodAfter: 5,
    notes: ''
  });

  // Problem Solving State
  const [problemSolving, setProblemSolving] = useState({
    problemDefinition: '',
    goals: [],
    brainstormedSolutions: [],
    prosAndCons: {},
    chosenSolution: '',
    actionPlan: [],
    outcomes: ''
  });

  // Available emotions for selection
  const emotionsList = [
    'Anxious', 'Sad', 'Angry', 'Frustrated', 'Happy', 
    'Excited', 'Guilty', 'Ashamed', 'Hopeless', 'Content',
    'Worried', 'Fearful', 'Overwhelmed', 'Lonely', 'Grateful'
  ];

  // Available activities for behavioral activation
  const suggestedActivities = [
    { name: 'Take a walk', category: 'exercise', icon: '🚶' },
    { name: 'Call a friend', category: 'social', icon: '📞' },
    { name: 'Read a book', category: 'leisure', icon: '📚' },
    { name: 'Practice mindfulness', category: 'mindfulness', icon: '🧘' },
    { name: 'Cook a meal', category: 'self-care', icon: '🍳' },
    { name: 'Listen to music', category: 'leisure', icon: '🎵' },
    { name: 'Write in journal', category: 'reflection', icon: '✍️' },
    { name: 'Exercise', category: 'exercise', icon: '💪' },
    { name: 'Watch a movie', category: 'leisure', icon: '🎬' },
    { name: 'Garden', category: 'hobby', icon: '🌱' }
  ];

  // Worksheet templates configuration
  const worksheetTypes = [
    {
      id: 'thoughtRecord',
      name: 'Thought Record',
      icon: '💭',
      description: 'Challenge negative thoughts and develop balanced perspectives',
      color: 'bg-blue-500'
    },
    {
      id: 'abcModel',
      name: 'ABC Model',
      icon: '🔄',
      description: 'Analyze beliefs and their consequences',
      color: 'bg-purple-500'
    },
    {
      id: 'behavioralActivation',
      name: 'Behavioral Activation',
      icon: '🎯',
      description: 'Plan and track mood-boosting activities',
      color: 'bg-green-500'
    },
    {
      id: 'problemSolving',
      name: 'Problem Solving',
      icon: '🧩',
      description: 'Systematic approach to solving challenges',
      color: 'bg-orange-500'
    }
  ];

  // Auto-save functionality
  useEffect(() => {
    if (autoSave) {
      const saveTimer = setTimeout(() => {
        saveWorksheet();
      }, 3000);
      return () => clearTimeout(saveTimer);
    }
  }, [thoughtRecord, abcModel, behavioralActivation, problemSolving, autoSave]);

  // Load saved worksheets on mount
  useEffect(() => {
    loadSavedWorksheets();
  }, []);

  // Functions
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const saveWorksheet = async () => {
    setIsSaving(true);
    try {
      const worksheetData = {
        type: activeWorksheet,
        data: getActiveWorksheetData(),
        timestamp: new Date().toISOString(),
        id: Date.now()
      };
      
      // In production, this would save to backend
      const existing = localStorage.getItem('cbtWorksheets') || '[]';
      const worksheets = JSON.parse(existing);
      worksheets.push(worksheetData);
      localStorage.setItem('cbtWorksheets', JSON.stringify(worksheets));
      
      showNotification('Worksheet saved successfully!');
    } catch (error) {
      showNotification('Error saving worksheet', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const loadSavedWorksheets = () => {
    try {
      const saved = localStorage.getItem('cbtWorksheets') || '[]';
      setSavedWorksheets(JSON.parse(saved));
    } catch (error) {
      console.error('Error loading worksheets:', error);
    }
  };

  const getActiveWorksheetData = () => {
    switch (activeWorksheet) {
      case 'thoughtRecord':
        return thoughtRecord;
      case 'abcModel':
        return abcModel;
      case 'behavioralActivation':
        return behavioralActivation;
      case 'problemSolving':
        return problemSolving;
      default:
        return {};
    }
  };

  const exportWorksheet = () => {
    const data = getActiveWorksheetData();
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cbt-worksheet-${activeWorksheet}-${Date.now()}.json`;
    a.click();
    showNotification('Worksheet exported!');
  };

  const printWorksheet = () => {
    window.print();
    showNotification('Preparing worksheet for printing...');
  };

  // Render Thought Record Worksheet
  const renderThoughtRecord = () => (
    <div className="space-y-6">
      {/* Situation Section */}
      <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm">
        <h3 className="text-lg font-semibold mb-4 text-gray-800">
          <i className="fas fa-calendar-alt mr-2 text-blue-500"></i>
          Situation
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <input
            type="date"
            value={thoughtRecord.date}
            onChange={(e) => setThoughtRecord({ ...thoughtRecord, date: e.target.value })}
            className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
          />
          <input
            type="time"
            value={thoughtRecord.time}
            onChange={(e) => setThoughtRecord({ ...thoughtRecord, time: e.target.value })}
            className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <textarea
          placeholder="Describe the situation that triggered your thoughts..."
          value={thoughtRecord.situation}
          onChange={(e) => setThoughtRecord({ ...thoughtRecord, situation: e.target.value })}
          className="w-full mt-4 p-4 border rounded-lg resize-none h-24 focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* Automatic Thoughts Section */}
      <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm">
        <h3 className="text-lg font-semibold mb-4 text-gray-800">
          <i className="fas fa-brain mr-2 text-purple-500"></i>
          Automatic Thoughts
        </h3>
        <textarea
          placeholder="What thoughts went through your mind? (Be specific)"
          value={thoughtRecord.automaticThoughts}
          onChange={(e) => setThoughtRecord({ ...thoughtRecord, automaticThoughts: e.target.value })}
          className="w-full p-4 border rounded-lg resize-none h-32 focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* Emotions Section */}
      <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm">
        <h3 className="text-lg font-semibold mb-4 text-gray-800">
          <i className="fas fa-heart mr-2 text-red-500"></i>
          Emotions & Intensity
        </h3>
        <div className="flex flex-wrap gap-2 mb-4">
          {emotionsList.map((emotion) => (
            <button
              key={emotion}
              onClick={() => {
                const newEmotions = thoughtRecord.emotions.includes(emotion)
                  ? thoughtRecord.emotions.filter(e => e !== emotion)
                  : [...thoughtRecord.emotions, emotion];
                setThoughtRecord({ ...thoughtRecord, emotions: newEmotions });
              }}
              className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                thoughtRecord.emotions.includes(emotion)
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {emotion}
            </button>
          ))}
        </div>
        
        {thoughtRecord.emotions.length > 0 && (
          <div className="space-y-3 mt-4">
            {thoughtRecord.emotions.map((emotion) => (
              <div key={emotion} className="flex items-center space-x-4">
                <span className="w-24 text-sm font-medium">{emotion}</span>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={thoughtRecord.emotionIntensity[emotion] || 50}
                  onChange={(e) => setThoughtRecord({
                    ...thoughtRecord,
                    emotionIntensity: {
                      ...thoughtRecord.emotionIntensity,
                      [emotion]: parseInt(e.target.value)
                    }
                  })}
                  className="flex-1"
                />
                <span className="w-12 text-center font-semibold">
                  {thoughtRecord.emotionIntensity[emotion] || 50}%
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Evidence Section */}
      <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm">
        <h3 className="text-lg font-semibold mb-4 text-gray-800">
          <i className="fas fa-balance-scale mr-2 text-green-500"></i>
          Evidence Analysis
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2 text-gray-700">
              Evidence FOR the thought
            </label>
            <textarea
              placeholder="What evidence supports this thought?"
              value={thoughtRecord.evidence.for}
              onChange={(e) => setThoughtRecord({
                ...thoughtRecord,
                evidence: { ...thoughtRecord.evidence, for: e.target.value }
              })}
              className="w-full p-3 border rounded-lg resize-none h-32 focus:ring-2 focus:ring-green-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2 text-gray-700">
              Evidence AGAINST the thought
            </label>
            <textarea
              placeholder="What evidence contradicts this thought?"
              value={thoughtRecord.evidence.against}
              onChange={(e) => setThoughtRecord({
                ...thoughtRecord,
                evidence: { ...thoughtRecord.evidence, against: e.target.value }
              })}
              className="w-full p-3 border rounded-lg resize-none h-32 focus:ring-2 focus:ring-red-500"
            />
          </div>
        </div>
      </div>

      {/* Balanced Thought Section */}
      <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm">
        <h3 className="text-lg font-semibold mb-4 text-gray-800">
          <i className="fas fa-lightbulb mr-2 text-yellow-500"></i>
          Balanced Thought
        </h3>
        <textarea
          placeholder="Based on the evidence, what's a more balanced way to think about this?"
          value={thoughtRecord.balancedThought}
          onChange={(e) => setThoughtRecord({ ...thoughtRecord, balancedThought: e.target.value })}
          className="w-full p-4 border rounded-lg resize-none h-24 focus:ring-2 focus:ring-yellow-500"
        />
      </div>

      {/* Emotion Reassessment */}
      {thoughtRecord.emotions.length > 0 && thoughtRecord.balancedThought && (
        <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm">
          <h3 className="text-lg font-semibold mb-4 text-gray-800">
            <i className="fas fa-sync-alt mr-2 text-blue-500"></i>
            Re-rate Your Emotions
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            After considering the balanced thought, how intense are your emotions now?
          </p>
          <div className="space-y-3">
            {thoughtRecord.emotions.map((emotion) => (
              <div key={`reassess-${emotion}`} className="flex items-center space-x-4">
                <span className="w-24 text-sm font-medium">{emotion}</span>
                <span className="text-xs text-gray-500">Before: {thoughtRecord.emotionIntensity[emotion] || 50}%</span>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={thoughtRecord.emotionReassessment[emotion] || thoughtRecord.emotionIntensity[emotion] || 50}
                  onChange={(e) => setThoughtRecord({
                    ...thoughtRecord,
                    emotionReassessment: {
                      ...thoughtRecord.emotionReassessment,
                      [emotion]: parseInt(e.target.value)
                    }
                  })}
                  className="flex-1"
                />
                <span className="w-12 text-center font-semibold text-green-600">
                  {thoughtRecord.emotionReassessment[emotion] || thoughtRecord.emotionIntensity[emotion] || 50}%
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Action Plan */}
      <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm">
        <h3 className="text-lg font-semibold mb-4 text-gray-800">
          <i className="fas fa-tasks mr-2 text-indigo-500"></i>
          Behavioral Plan
        </h3>
        <textarea
          placeholder="What can you do differently next time? What actions will you take?"
          value={thoughtRecord.behavioralPlan}
          onChange={(e) => setThoughtRecord({ ...thoughtRecord, behavioralPlan: e.target.value })}
          className="w-full p-4 border rounded-lg resize-none h-24 focus:ring-2 focus:ring-indigo-500"
        />
      </div>
    </div>
  );

  // Render ABC Model Worksheet
  const renderABCModel = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm">
        <h3 className="text-lg font-semibold mb-4 text-gray-800">
          <span className="inline-block w-8 h-8 bg-red-500 text-white rounded-full text-center leading-8 mr-2">A</span>
          Activating Event
        </h3>
        <textarea
          placeholder="Describe the event or situation that triggered your response..."
          value={abcModel.activatingEvent}
          onChange={(e) => setAbcModel({ ...abcModel, activatingEvent: e.target.value })}
          className="w-full p-4 border rounded-lg resize-none h-32 focus:ring-2 focus:ring-red-500"
        />
      </div>

      <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm">
        <h3 className="text-lg font-semibold mb-4 text-gray-800">
          <span className="inline-block w-8 h-8 bg-orange-500 text-white rounded-full text-center leading-8 mr-2">B</span>
          Beliefs
        </h3>
        <textarea
          placeholder="What beliefs or thoughts did you have about the event?"
          value={abcModel.beliefs}
          onChange={(e) => setAbcModel({ ...abcModel, beliefs: e.target.value })}
          className="w-full p-4 border rounded-lg resize-none h-32 focus:ring-2 focus:ring-orange-500"
        />
      </div>

      <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm">
        <h3 className="text-lg font-semibold mb-4 text-gray-800">
          <span className="inline-block w-8 h-8 bg-yellow-500 text-white rounded-full text-center leading-8 mr-2">C</span>
          Consequences
        </h3>
        <textarea
          placeholder="What were the emotional and behavioral consequences?"
          value={abcModel.consequences}
          onChange={(e) => setAbcModel({ ...abcModel, consequences: e.target.value })}
          className="w-full p-4 border rounded-lg resize-none h-32 focus:ring-2 focus:ring-yellow-500"
        />
      </div>

      <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm">
        <h3 className="text-lg font-semibold mb-4 text-gray-800">
          <span className="inline-block w-8 h-8 bg-green-500 text-white rounded-full text-center leading-8 mr-2">D</span>
          Dispute
        </h3>
        <textarea
          placeholder="Challenge the beliefs: Are they realistic? Helpful? Based on facts?"
          value={abcModel.dispute}
          onChange={(e) => setAbcModel({ ...abcModel, dispute: e.target.value })}
          className="w-full p-4 border rounded-lg resize-none h-32 focus:ring-2 focus:ring-green-500"
        />
      </div>

      <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm">
        <h3 className="text-lg font-semibold mb-4 text-gray-800">
          <span className="inline-block w-8 h-8 bg-blue-500 text-white rounded-full text-center leading-8 mr-2">E</span>
          Effective New Belief
        </h3>
        <textarea
          placeholder="What's a more balanced and helpful belief?"
          value={abcModel.effectiveNewBelief}
          onChange={(e) => setAbcModel({ ...abcModel, effectiveNewBelief: e.target.value })}
          className="w-full p-4 border rounded-lg resize-none h-32 focus:ring-2 focus:ring-blue-500"
        />
      </div>
    </div>
  );

  // Render Behavioral Activation Worksheet
  const renderBehavioralActivation = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm">
        <h3 className="text-lg font-semibold mb-4 text-gray-800">
          <i className="fas fa-chart-line mr-2 text-green-500"></i>
          Mood Before Activities
        </h3>
        <div className="flex items-center space-x-4">
          <span className="text-2xl">😔</span>
          <input
            type="range"
            min="1"
            max="10"
            value={behavioralActivation.moodBefore}
            onChange={(e) => setBehavioralActivation({
              ...behavioralActivation,
              moodBefore: parseInt(e.target.value)
            })}
            className="flex-1"
          />
          <span className="text-2xl">😊</span>
          <span className="w-12 text-center font-bold text-lg">
            {behavioralActivation.moodBefore}/10
          </span>
        </div>
      </div>

      <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm">
        <h3 className="text-lg font-semibold mb-4 text-gray-800">
          <i className="fas fa-calendar-check mr-2 text-blue-500"></i>
          Planned Activities
        </h3>
        
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
          {suggestedActivities.map((activity) => (
            <button
              key={activity.name}
              onClick={() => {
                const newActivities = behavioralActivation.activities.some(a => a.name === activity.name)
                  ? behavioralActivation.activities.filter(a => a.name !== activity.name)
                  : [...behavioralActivation.activities, { ...activity, completed: false }];
                setBehavioralActivation({
                  ...behavioralActivation,
                  activities: newActivities
                });
              }}
              className={`p-3 rounded-lg border-2 transition-all ${
                behavioralActivation.activities.some(a => a.name === activity.name)
                  ? 'border-green-500 bg-green-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="text-2xl mb-1">{activity.icon}</div>
              <div className="text-sm">{activity.name}</div>
            </button>
          ))}
        </div>

        <div className="mt-4">
          <input
            type="text"
            placeholder="Add custom activity..."
            onKeyPress={(e) => {
              if (e.key === 'Enter' && e.target.value) {
                setBehavioralActivation({
                  ...behavioralActivation,
                  activities: [...behavioralActivation.activities, {
                    name: e.target.value,
                    category: 'custom',
                    icon: '⭐',
                    completed: false
                  }]
                });
                e.target.value = '';
              }
            }}
            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {behavioralActivation.activities.length > 0 && (
        <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm">
          <h3 className="text-lg font-semibold mb-4 text-gray-800">
            <i className="fas fa-check-circle mr-2 text-green-500"></i>
            Track Completion
          </h3>
          <div className="space-y-2">
            {behavioralActivation.activities.map((activity, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={activity.completed}
                    onChange={() => {
                      const newActivities = [...behavioralActivation.activities];
                      newActivities[index].completed = !newActivities[index].completed;
                      setBehavioralActivation({
                        ...behavioralActivation,
                        activities: newActivities
                      });
                    }}
                    className="w-5 h-5 rounded text-green-500"
                  />
                  <span className="text-xl">{activity.icon}</span>
                  <span className={activity.completed ? 'line-through text-gray-500' : ''}>
                    {activity.name}
                  </span>
                </div>
                <button
                  onClick={() => {
                    setBehavioralActivation({
                      ...behavioralActivation,
                      activities: behavioralActivation.activities.filter((_, i) => i !== index)
                    });
                  }}
                  className="text-red-500 hover:text-red-700"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm">
        <h3 className="text-lg font-semibold mb-4 text-gray-800">
          <i className="fas fa-chart-line mr-2 text-blue-500"></i>
          Mood After Activities
        </h3>
        <div className="flex items-center space-x-4">
          <span className="text-2xl">😔</span>
          <input
            type="range"
            min="1"
            max="10"
            value={behavioralActivation.moodAfter}
            onChange={(e) => setBehavioralActivation({
              ...behavioralActivation,
              moodAfter: parseInt(e.target.value)
            })}
            className="flex-1"
          />
          <span className="text-2xl">😊</span>
          <span className="w-12 text-center font-bold text-lg">
            {behavioralActivation.moodAfter}/10
          </span>
        </div>
        
        {behavioralActivation.moodAfter > behavioralActivation.moodBefore && (
          <div className="mt-4 p-3 bg-green-100 rounded-lg text-green-700">
            <i className="fas fa-arrow-up mr-2"></i>
            Great job! Your mood improved by {behavioralActivation.moodAfter - behavioralActivation.moodBefore} points!
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm">
        <h3 className="text-lg font-semibold mb-4 text-gray-800">
          <i className="fas fa-sticky-note mr-2 text-purple-500"></i>
          Reflection Notes
        </h3>
        <textarea
          placeholder="What did you notice? Which activities helped most? Any insights?"
          value={behavioralActivation.notes}
          onChange={(e) => setBehavioralActivation({ ...behavioralActivation, notes: e.target.value })}
          className="w-full p-4 border rounded-lg resize-none h-24 focus:ring-2 focus:ring-purple-500"
        />
      </div>
    </div>
  );

  // Render Problem Solving Worksheet
  const renderProblemSolving = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm">
        <h3 className="text-lg font-semibold mb-4 text-gray-800">
          <i className="fas fa-exclamation-circle mr-2 text-red-500"></i>
          Problem Definition
        </h3>
        <textarea
          placeholder="Clearly define the problem you want to solve..."
          value={problemSolving.problemDefinition}
          onChange={(e) => setProblemSolving({ ...problemSolving, problemDefinition: e.target.value })}
          className="w-full p-4 border rounded-lg resize-none h-24 focus:ring-2 focus:ring-red-500"
        />
      </div>

      <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm">
        <h3 className="text-lg font-semibold mb-4 text-gray-800">
          <i className="fas fa-bullseye mr-2 text-blue-500"></i>
          Goals
        </h3>
        <div className="space-y-2">
          {problemSolving.goals.map((goal, index) => (
            <div key={index} className="flex items-center space-x-2">
              <input
                type="text"
                value={goal}
                onChange={(e) => {
                  const newGoals = [...problemSolving.goals];
                  newGoals[index] = e.target.value;
                  setProblemSolving({ ...problemSolving, goals: newGoals });
                }}
                className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              <button
                onClick={() => {
                  setProblemSolving({
                    ...problemSolving,
                    goals: problemSolving.goals.filter((_, i) => i !== index)
                  });
                }}
                className="text-red-500 hover:text-red-700"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
          ))}
          <button
            onClick={() => setProblemSolving({
              ...problemSolving,
              goals: [...problemSolving.goals, '']
            })}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition"
          >
            <i className="fas fa-plus mr-2"></i>
            Add Goal
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm">
        <h3 className="text-lg font-semibold mb-4 text-gray-800">
          <i className="fas fa-lightbulb mr-2 text-yellow-500"></i>
          Brainstorm Solutions
        </h3>
        <div className="space-y-2">
          {problemSolving.brainstormedSolutions.map((solution, index) => (
            <div key={index} className="flex items-center space-x-2">
              <span className="w-8 h-8 bg-yellow-100 rounded-full text-center leading-8 text-sm font-semibold">
                {index + 1}
              </span>
              <input
                type="text"
                value={solution}
                onChange={(e) => {
                  const newSolutions = [...problemSolving.brainstormedSolutions];
                  newSolutions[index] = e.target.value;
                  setProblemSolving({ ...problemSolving, brainstormedSolutions: newSolutions });
                }}
                className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-yellow-500"
              />
              <button
                onClick={() => {
                  setProblemSolving({
                    ...problemSolving,
                    brainstormedSolutions: problemSolving.brainstormedSolutions.filter((_, i) => i !== index)
                  });
                }}
                className="text-red-500 hover:text-red-700"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
          ))}
          <button
            onClick={() => setProblemSolving({
              ...problemSolving,
              brainstormedSolutions: [...problemSolving.brainstormedSolutions, '']
            })}
            className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition"
          >
            <i className="fas fa-plus mr-2"></i>
            Add Solution
          </button>
        </div>
      </div>

      {problemSolving.brainstormedSolutions.length > 0 && (
        <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm">
          <h3 className="text-lg font-semibold mb-4 text-gray-800">
            <i className="fas fa-balance-scale mr-2 text-purple-500"></i>
            Evaluate Solutions
          </h3>
          <div className="space-y-4">
            {problemSolving.brainstormedSolutions.filter(s => s).map((solution, index) => (
              <div key={index} className="border rounded-lg p-4">
                <h4 className="font-semibold mb-2">{solution}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm text-gray-600">Pros</label>
                    <textarea
                      placeholder="List advantages..."
                      value={problemSolving.prosAndCons[solution]?.pros || ''}
                      onChange={(e) => setProblemSolving({
                        ...problemSolving,
                        prosAndCons: {
                          ...problemSolving.prosAndCons,
                          [solution]: {
                            ...problemSolving.prosAndCons[solution],
                            pros: e.target.value
                          }
                        }
                      })}
                      className="w-full mt-1 p-2 border rounded resize-none h-20 text-sm focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">Cons</label>
                    <textarea
                      placeholder="List disadvantages..."
                      value={problemSolving.prosAndCons[solution]?.cons || ''}
                      onChange={(e) => setProblemSolving({
                        ...problemSolving,
                        prosAndCons: {
                          ...problemSolving.prosAndCons,
                          [solution]: {
                            ...problemSolving.prosAndCons[solution],
                            cons: e.target.value
                          }
                        }
                      })}
                      className="w-full mt-1 p-2 border rounded resize-none h-20 text-sm focus:ring-2 focus:ring-red-500"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm">
        <h3 className="text-lg font-semibold mb-4 text-gray-800">
          <i className="fas fa-check-circle mr-2 text-green-500"></i>
          Chosen Solution
        </h3>
        <select
          value={problemSolving.chosenSolution}
          onChange={(e) => setProblemSolving({ ...problemSolving, chosenSolution: e.target.value })}
          className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
        >
          <option value="">Select the best solution...</option>
          {problemSolving.brainstormedSolutions.filter(s => s).map((solution, index) => (
            <option key={index} value={solution}>{solution}</option>
          ))}
        </select>
      </div>

      {problemSolving.chosenSolution && (
        <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm">
          <h3 className="text-lg font-semibold mb-4 text-gray-800">
            <i className="fas fa-list-ol mr-2 text-indigo-500"></i>
            Action Plan
          </h3>
          <div className="space-y-2">
            {problemSolving.actionPlan.map((step, index) => (
              <div key={index} className="flex items-center space-x-2">
                <span className="w-8 h-8 bg-indigo-100 rounded-full text-center leading-8 text-sm font-semibold">
                  {index + 1}
                </span>
                <input
                  type="text"
                  value={step}
                  placeholder="Action step..."
                  onChange={(e) => {
                    const newPlan = [...problemSolving.actionPlan];
                    newPlan[index] = e.target.value;
                    setProblemSolving({ ...problemSolving, actionPlan: newPlan });
                  }}
                  className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
                <button
                  onClick={() => {
                    setProblemSolving({
                      ...problemSolving,
                      actionPlan: problemSolving.actionPlan.filter((_, i) => i !== index)
                    });
                  }}
                  className="text-red-500 hover:text-red-700"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
            ))}
            <button
              onClick={() => setProblemSolving({
                ...problemSolving,
                actionPlan: [...problemSolving.actionPlan, '']
              })}
              className="px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition"
            >
              <i className="fas fa-plus mr-2"></i>
              Add Step
            </button>
          </div>
        </div>
      )}
    </div>
  );

  // Main render
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <button
                onClick={() => navigate(-1)}
                className="mr-4 text-gray-600 hover:text-gray-900 transition"
              >
                <i className="fas fa-arrow-left text-lg"></i>
              </button>
              <h1 className="text-xl font-bold text-gray-900">CBT Worksheets</h1>
            </div>
            
            <div className="flex items-center space-x-2 sm:space-x-4">
              <button
                onClick={printWorksheet}
                className="p-2 text-gray-600 hover:text-gray-900 transition"
                title="Print"
              >
                <i className="fas fa-print"></i>
              </button>
              <button
                onClick={exportWorksheet}
                className="p-2 text-gray-600 hover:text-gray-900 transition"
                title="Export"
              >
                <i className="fas fa-download"></i>
              </button>
              <button
                onClick={saveWorksheet}
                disabled={isSaving}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition disabled:bg-gray-300"
              >
                {isSaving ? (
                  <i className="fas fa-spinner fa-spin"></i>
                ) : (
                  <>
                    <i className="fas fa-save mr-2"></i>
                    Save
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Worksheet Type Selector - Tablet optimized */}
      <div className="bg-white border-b px-4 py-3 overflow-x-auto">
        <div className="flex space-x-2 min-w-max">
          {worksheetTypes.map((type) => (
            <button
              key={type.id}
              onClick={() => setActiveWorksheet(type.id)}
              className={`flex items-center px-4 py-2 rounded-lg transition-all ${
                activeWorksheet === type.id
                  ? `${type.color} text-white shadow-md`
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <span className="text-xl mr-2">{type.icon}</span>
              <div className="text-left">
                <div className="font-semibold">{type.name}</div>
                <div className="text-xs opacity-90">{type.description}</div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Notification Toast */}
      {notification && (
        <div className={`fixed top-20 right-4 z-50 p-4 rounded-lg shadow-lg ${
          notification.type === 'success' ? 'bg-green-500' : 'bg-red-500'
        } text-white transform transition-transform duration-300`}>
          <i className={`fas ${notification.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'} mr-2`}></i>
          {notification.message}
        </div>
      )}

      {/* Auto-save indicator */}
      <div className="fixed bottom-4 right-4 z-40">
        <label className="flex items-center bg-white px-4 py-2 rounded-lg shadow-md">
          <input
            type="checkbox"
            checked={autoSave}
            onChange={(e) => setAutoSave(e.target.checked)}
            className="mr-2"
          />
          <span className="text-sm text-gray-700">Auto-save</span>
          {autoSave && (
            <span className="ml-2 text-xs text-green-600">
              <i className="fas fa-check-circle"></i>
            </span>
          )}
        </label>
      </div>

      {/* Main Content Area */}
      <div className="max-w-4xl mx-auto px-4 py-6" ref={worksheetRef}>
        {activeWorksheet === 'thoughtRecord' && renderThoughtRecord()}
        {activeWorksheet === 'abcModel' && renderABCModel()}
        {activeWorksheet === 'behavioralActivation' && renderBehavioralActivation()}
        {activeWorksheet === 'problemSolving' && renderProblemSolving()}
      </div>

      {/* Saved Worksheets Section */}
      {savedWorksheets.length > 0 && (
        <div className="max-w-4xl mx-auto px-4 pb-8">
          <div className="bg-white rounded-lg p-6 shadow-sm">
            <h3 className="text-lg font-semibold mb-4 text-gray-800">
              <i className="fas fa-history mr-2 text-blue-500"></i>
              Previously Saved Worksheets
            </h3>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {savedWorksheets.slice(-5).reverse().map((worksheet) => (
                <div
                  key={worksheet.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition cursor-pointer"
                  onClick={() => {
                    const type = worksheetTypes.find(t => t.id === worksheet.type);
                    showNotification(`Loading ${type.name} from ${new Date(worksheet.timestamp).toLocaleDateString()}`);
                  }}
                >
                  <div className="flex items-center">
                    <span className="text-xl mr-3">
                      {worksheetTypes.find(t => t.id === worksheet.type)?.icon}
                    </span>
                    <div>
                      <div className="font-medium">
                        {worksheetTypes.find(t => t.id === worksheet.type)?.name}
                      </div>
                      <div className="text-xs text-gray-500">
                        {new Date(worksheet.timestamp).toLocaleString()}
                      </div>
                    </div>
                  </div>
                  <i className="fas fa-chevron-right text-gray-400"></i>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CBTWorksheet;